import { FAMILY, FAMILY_ORDER, SCENARIOS } from '../data';

import { computeScore } from './scoring';
import { FamilyMember, FamilyMemberId, GameEvent, GameState } from './types';

export type VerdictLine = {
  memberId: FamilyMemberId;
  name: string;
  text: string;
};

export type FamilyVerdict = {
  /** one line per family member, in display order */
  lines: VerdictLine[];
  /** Blackout Parenting rating, 1–10, derived from score + mood — the score engine is untouched */
  rating: number;
};

/** an ask is an event with an explicitly marked hoped-for answer — acks, warnings, and jokes have none */
const isAsk = (event: GameEvent): boolean => event.responses.some(({ grantsAsk }) => grantsAsk);

/** each member's personal disaster trumps the tally (Sam: dark house, Riley: Wi-Fi, Jojo: fridge) */
const specialHappened = (memberId: FamilyMemberId, state: GameState): boolean => {
  if (memberId === 'sam') return state.blackoutMinutes > 0;
  if (memberId === 'riley') return state.firedEventIds.includes('sys-wifi-grumble');
  return state.firedEventIds.includes('sys-food-spoiled');
};

const verdictText = (member: FamilyMember, state: GameState): string => {
  if (specialHappened(member.id, state)) return member.verdicts.special;

  const { events } = SCENARIOS[state.prep.scenarioId];
  const asks = state.decisions.flatMap((decision) => {
    const event = events.find(({ id }) => id === decision.eventId);
    return event && event.from === member.name && isAsk(event) ? [{ decision, event }] : [];
  });
  if (asks.length === 0) return member.verdicts.mixed;

  const granted = asks.filter(({ decision, event }) => event.responses[decision.responseIndex]?.grantsAsk).length;
  if (granted === asks.length) return member.verdicts.granted;
  if (granted === 0) return member.verdicts.denied;
  return member.verdicts.mixed;
};

export const computeFamilyVerdict = (state: GameState): FamilyVerdict => {
  const score = computeScore(state);
  const rating = Math.max(1, Math.min(10, Math.round((0.6 * score.total + 0.4 * score.avgMood) / 10)));
  const lines = FAMILY_ORDER.map((memberId) => {
    const member = FAMILY[memberId];
    return { memberId, name: member.name, text: verdictText(member, state) };
  });
  return { lines, rating };
};
